import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, capitalize, Tab, Tabs } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { CriteriaOption } from 'commons/components/SearchRelated';
import { useUpdateQueryParams } from 'commons/components/SearchRelated/SearchWithCriteria/hooks';
import { BinNetsuiteSyncStatusEnum, FileImportType } from 'commons/enums';
import { IBin, IBinCommodity, ProductDetail } from 'commons/types';
import {
  deleteBinCommodityAction,
  getBinCommoditiesAction,
  getBinListAction,
  getBinListWithSkuAction,
  getBulkUploadHistoryAction,
  updateBinCommodityStatusAction,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import {
  CustomTabPanel,
  FileImportTab,
  ListFilterCard,
  ListPage,
} from '../commons/components';

import BinListTable from './components/BinListTable.component';
import BinTable from './components/BinTable.component';
import CommodityTable from './components/CommodityTable.component';
import { CreateBinDialog } from './components/CreateBinDialog.component';
import { CreateCommodityDialog } from './components/CreateCommodityDialog.component';
import { EditBinCommodityDialog } from './components/EditBinCommodityDialog.component';
import { EditBinInventoryNumberDialog } from './components/EditBinInventoryNumberDialog.component';
import { EditBinQtyDialog } from './components/EditBinQtyDialog.component';
import { EditBinTypeDialog } from './components/EditBinTypeDialog.component';
import { EditCommodityNameDialog } from './components/EditCommodityNameDialog.component';
import { BinBulkUploadTemplate } from './templates';
import { IEditableBin } from './types';

const getNSJobStatusURL = (): string | null => {
  const netsuiteUrl = process.env.REACT_APP_NETSUITE_URL;
  if (!netsuiteUrl) return null;
  return `${netsuiteUrl}/app/setup/upload/csv/csvstatus.nl?&whence=`;
};
const NETSUITE_JOB_STATUS_URL = getNSJobStatusURL();

const MasterBinPage: React.FC = () => {
  const { binList, binSkuList, binCommodityList, selectedLocation } =
    useSelector((state: RootReducerInterface) => state.dashboard);

  const dispatch = useDispatch<AppDispatch>();
  const { updateQueryParams } = useUpdateQueryParams();

  const BIN_LIST_TAB_INDEX = 0;
  const BIN_SKU_LIST_TAB_INDEX = 1;
  const BULK_UPLOAD_TAB_INDEX = 2;
  const COMMODITIES_TAB_INDEX = 3;

  const [tabValue, setTabValue] = useState(BIN_LIST_TAB_INDEX);

  const setTabQueryParams = (newTabValue: number): void => {
    if (newTabValue === BIN_SKU_LIST_TAB_INDEX) {
      updateQueryParams({ bin_view: 'bin_sku' }, { clearOther: true });
      return;
    }
    if (newTabValue === BIN_LIST_TAB_INDEX) {
      updateQueryParams({}, { clearOther: true });
    }
  };

  const handleTabChange = (
    event: React.SyntheticEvent,
    newTabValue: number,
  ) => {
    setTabValue(newTabValue);
    setTabQueryParams(newTabValue);
  };

  useEffect(() => {
    setTabQueryParams(tabValue);
  }, []);

  useEffect(() => {
    dispatch(getBinCommoditiesAction());
  }, [dispatch]);

  const updateBinListSearchDebounce = useDebouncedCallback(
    (_searchParams, _location, _dispatch) => {
      if (_searchParams.get('file_import_type')) {
        _dispatch(
          getBulkUploadHistoryAction({
            searchParams: _searchParams,
            locationId: _location,
          }),
        );
        return;
      }
      if (_searchParams.get('bin_view') === 'bin_sku') {
        _dispatch(
          getBinListWithSkuAction({
            searchParams: _searchParams,
            locationId: _location,
          }),
        );
        return;
      }
      _dispatch(
        getBinListAction({
          searchParams: _searchParams,
          locationId: _location,
        }),
      );
    },
    200,
    { maxWait: 1000 },
  );

  const binListSearchCriteria: CriteriaOption[] = [
    {
      name: 'Bin Number',
      id: 'bin',
      isArray: true,
      parameterMapping: 'bin_names',
    },
  ];

  const netsuiteSyncStatusArrayText: {
    key: BinNetsuiteSyncStatusEnum;
    text: string;
  }[] = Object.values(BinNetsuiteSyncStatusEnum).map((value) => ({
    key: value,
    text: capitalize(value.replace('-', ' ')),
  }));

  const [netsuiteSyncStatusFilter, setNetsuiteSyncStatusFilter] =
    useState<BinNetsuiteSyncStatusEnum | null>(null);

  const handleNetsuiteSyncStatusFilter = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: BinNetsuiteSyncStatusEnum | null,
  ): void => {
    setNetsuiteSyncStatusFilter(value);
    updateQueryParams(
      { netsuite_sync_status: value },
      { clearQueryParams: ['page'] },
    );
  };

  const searchCriteria: CriteriaOption[] = [
    {
      name: 'Bin Number',
      id: 'bin',
      isArray: true,
      parameterMapping: 'bin_numbers',
    },
    {
      name: 'SKU Code',
      id: 'sku_code',
      isArray: true,
      parameterMapping: 'sku_codes',
    },
  ];

  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [openEditQtyModal, setOpenEditQtyModal] = useState<boolean>(false);
  const [openEditInventoryNumberModal, setOpenEditInventoryNumberModal] =
    useState<boolean>(false);
  const [selectedBin, setSelectedBin] = useState<ProductDetail | null>(null);

  const [openBinListEditTypeModal, setOpenBinListEditTypeModal] =
    useState<boolean>(false);
  const [openBinListEditCommodityModal, setOpenBinListEditCommodityModal] =
    useState<boolean>(false);
  const [selectedBinListItem, setSelectedBinListItem] =
    useState<IEditableBin | null>(null);

  const [openCreateCommodityModal, setOpenCreateCommodityModal] =
    useState<boolean>(false);

  const [openEditCommodityNameModal, setOpenEditCommodityNameModal] =
    useState<boolean>(false);
  const [selectedCommodity, setSelectedCommodity] =
    useState<IBinCommodity | null>(null);

  const [openToggleCommodityStatusModal, setOpenToggleCommodityStatusModal] =
    useState<boolean>(false);
  const [commodityToToggle, setCommodityToToggle] =
    useState<IBinCommodity | null>(null);

  const [openDeleteCommodityModal, setOpenDeleteCommodityModal] =
    useState<boolean>(false);
  const [commodityToDelete, setCommodityToDelete] =
    useState<IBinCommodity | null>(null);

  const onCreateModalOpen = () => setOpenCreateModal(true);
  const onCreateModalClose = () => setOpenCreateModal(false);

  const onCreateCommodityModalOpen = () => setOpenCreateCommodityModal(true);
  const onCreateCommodityModalClose = () => setOpenCreateCommodityModal(false);

  const onEditCommodityNameModalOpen = (commodity: IBinCommodity) => {
    setSelectedCommodity(commodity);
    setOpenEditCommodityNameModal(true);
  };
  const onEditCommodityNameModalClose = () => {
    setOpenEditCommodityNameModal(false);
    setSelectedCommodity(null);
  };

  const onToggleCommodityStatusModalOpen = (commodity: IBinCommodity) => {
    setCommodityToToggle(commodity);
    setOpenToggleCommodityStatusModal(true);
  };
  const onToggleCommodityStatusModalClose = () => {
    setOpenToggleCommodityStatusModal(false);
    setCommodityToToggle(null);
  };
  const onToggleCommodityStatusConfirm = () => {
    if (commodityToToggle) {
      dispatch(
        updateBinCommodityStatusAction({
          id: commodityToToggle._id,
          name: commodityToToggle.name,
          isEnabled: !commodityToToggle.is_enabled,
        }),
      );
    }
  };

  const onDeleteCommodityModalOpen = (commodity: IBinCommodity) => {
    setCommodityToDelete(commodity);
    setOpenDeleteCommodityModal(true);
  };
  const onDeleteCommodityModalClose = () => {
    setOpenDeleteCommodityModal(false);
    setCommodityToDelete(null);
  };
  const onDeleteCommodityConfirm = () => {
    if (commodityToDelete) {
      dispatch(
        deleteBinCommodityAction({
          id: commodityToDelete._id,
          name: commodityToDelete.name,
        }),
      );
    }
  };

  const onEditQtyModalOpen = (bin: ProductDetail) => {
    setSelectedBin(bin);
    setOpenEditQtyModal(true);
  };
  const onEditQtyModalClose = () => {
    setOpenEditQtyModal(false);
    setSelectedBin(null);
  };

  const onEditInventoryNumberModalOpen = (bin: ProductDetail) => {
    setSelectedBin(bin);
    setOpenEditInventoryNumberModal(true);
  };
  const onEditInventoryNumberModalClose = () => {
    setOpenEditInventoryNumberModal(false);
    setSelectedBin(null);
  };

  const onBinListEditTypeModalOpen = (bin: IBin) => {
    setSelectedBinListItem({
      bin: bin.name,
      bin_type: bin.bin_type,
      commodity: bin.commodity,
    });
    setOpenBinListEditTypeModal(true);
  };
  const onBinListEditTypeModalClose = () => {
    setOpenBinListEditTypeModal(false);
    setSelectedBinListItem(null);
  };

  const onBinListEditCommodityModalOpen = (bin: IBin) => {
    setSelectedBinListItem({
      bin: bin.name,
      bin_type: bin.bin_type,
      commodity: bin.commodity,
    });
    setOpenBinListEditCommodityModal(true);
  };
  const onBinListEditCommodityModalClose = () => {
    setOpenBinListEditCommodityModal(false);
    setSelectedBinListItem(null);
  };

  return (
    <ListPage updateListSearchDebounce={updateBinListSearchDebounce}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Bin List" />
          <Tab label="Bin SKU List" />
          <Tab label="Bulk Upload Bins" />
          <Tab label="Commodities" />
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index={BIN_LIST_TAB_INDEX}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <ListFilterCard
            searchCriteria={binListSearchCriteria}
            statusLabel="NetSuite Sync Status"
            statusArrayText={netsuiteSyncStatusArrayText}
            statusFilter={netsuiteSyncStatusFilter}
            handleStatusFilter={handleNetsuiteSyncStatusFilter}
          />
          <BinListTable
            data={binList}
            location={selectedLocation?.warehouse_id}
            onClickCreate={onCreateModalOpen}
            onClickEditBinType={onBinListEditTypeModalOpen}
            onClickEditCommodity={onBinListEditCommodityModalOpen}
          />
          {openBinListEditTypeModal && selectedBinListItem && (
            <EditBinTypeDialog
              onClose={onBinListEditTypeModalClose}
              openModal={openBinListEditTypeModal}
              location={selectedLocation?.warehouse_id}
              binData={selectedBinListItem}
            />
          )}
          {openBinListEditCommodityModal && selectedBinListItem && (
            <EditBinCommodityDialog
              onClose={onBinListEditCommodityModalClose}
              openModal={openBinListEditCommodityModal}
              location={selectedLocation?.warehouse_id}
              binData={selectedBinListItem}
            />
          )}
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={BIN_SKU_LIST_TAB_INDEX}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <ListFilterCard searchCriteria={searchCriteria} />
          <BinTable
            data={binSkuList}
            onClickEditQty={onEditQtyModalOpen}
            onClickEditInventoryNumber={onEditInventoryNumberModalOpen}
          />
          {openEditQtyModal && selectedBin && (
            <EditBinQtyDialog
              onClose={onEditQtyModalClose}
              openModal={openEditQtyModal}
              location={selectedLocation?.warehouse_id}
              binData={selectedBin}
            />
          )}
          {openEditInventoryNumberModal && selectedBin && (
            <EditBinInventoryNumberDialog
              onClose={onEditInventoryNumberModalClose}
              openModal={openEditInventoryNumberModal}
              location={selectedLocation?.warehouse_id}
              binData={selectedBin}
            />
          )}
        </Box>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={BULK_UPLOAD_TAB_INDEX}>
        <FileImportTab
          onDownloadTemplateClick={() => {
            BinBulkUploadTemplate.createLink().click();
          }}
          downloadTemplateBtnText="Create/Update Bin"
          uploadType={FileImportType.BIN}
          infoMessage={
            <>
              Users with the Admin role can only upload bins for their own
              warehouse.
              {NETSUITE_JOB_STATUS_URL && (
                <>
                  <br />
                  To see actual job status progress in Netsuite, click{' '}
                  <a
                    href={NETSUITE_JOB_STATUS_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    here
                  </a>
                </>
              )}
            </>
          }
        />
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={COMMODITIES_TAB_INDEX}>
        <CommodityTable
          data={binCommodityList}
          onClickCreate={onCreateCommodityModalOpen}
          onClickEdit={onEditCommodityNameModalOpen}
          onClickToggleStatus={onToggleCommodityStatusModalOpen}
          onClickDelete={onDeleteCommodityModalOpen}
        />
        {openEditCommodityNameModal && selectedCommodity && (
          <EditCommodityNameDialog
            onClose={onEditCommodityNameModalClose}
            openModal={openEditCommodityNameModal}
            commodity={selectedCommodity}
          />
        )}
        {openToggleCommodityStatusModal && commodityToToggle && (
          <ConfirmationDialog
            title={
              commodityToToggle.is_enabled
                ? 'Disable Commodity'
                : 'Enable Commodity'
            }
            content={`Are you sure you want to ${
              commodityToToggle.is_enabled ? 'disable' : 'enable'
            } <b>${commodityToToggle.name}</b>?`}
            onClose={onToggleCommodityStatusModalClose}
            openModal={openToggleCommodityStatusModal}
            onConfirm={onToggleCommodityStatusConfirm}
          />
        )}
        {openDeleteCommodityModal && commodityToDelete && (
          <ConfirmationDialog
            title="Delete Commodity"
            content={`Are you sure you want to delete <b>${commodityToDelete.name}</b>? This action cannot be undone.`}
            onClose={onDeleteCommodityModalClose}
            openModal={openDeleteCommodityModal}
            onConfirm={onDeleteCommodityConfirm}
          />
        )}
      </CustomTabPanel>
      {openCreateModal && (
        <CreateBinDialog
          onClose={onCreateModalClose}
          openModal={openCreateModal}
          location={selectedLocation?.warehouse_id}
        />
      )}
      {openCreateCommodityModal && (
        <CreateCommodityDialog
          onClose={onCreateCommodityModalClose}
          openModal={openCreateCommodityModal}
        />
      )}
    </ListPage>
  );
};

export default MasterBinPage;
